import { logger } from "@/utils/debug";
import type { StateStorage } from "zustand/middleware";

/**
 * Custom IndexedDB storage adapter for Zustand persist middleware
 * Implements the StateStorage interface required by Zustand
 * Each store uses a separate IndexedDB object store for better organization
 */
class ZustandIndexedDBStorage implements StateStorage {
  private storeName: string;
  private dbName = "atlasio-store";
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  /**
   * @param storeName - The name of the IndexedDB object store to use
   *                    Valid values: "projects", "workspaces", "datasets"
   *                    Note: "projects" maps to "projects-state" store to avoid conflict with data store
   */
  constructor(storeName: "projects" | "workspaces" | "datasets") {
    this.storeName = storeName === "projects" ? "projects-state" : storeName;
  }

  /**
   * Initializes the IndexedDB connection
   * Always waits for the main IndexedDB service to initialize first
   * This ensures all stores are created by the main service and prevents race conditions
   */
  private async init(): Promise<void> {
    if (this.db) {
      // Check if the store exists
      if (this.db.objectStoreNames.contains(this.storeName)) {
        return;
      }
      this.db.close();
      this.db = null;
    }
    if (this.initPromise) return this.initPromise;

    // Always wait for the main IndexedDB service to initialize first
    // This ensures all stores are created properly and prevents race conditions
    const { indexedDBService } = await import("./indexedDB");
    await indexedDBService.init();

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);

      request.onerror = () => {
        const error = request.error;
        logger.error("[ZustandIndexedDB] init: Error", error);
        this.initPromise = null;
        reject(error || new Error("Failed to open database"));
      };

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          logger.warn(
            "[ZustandIndexedDB] init: Store missing in onupgradeneeded, creating",
            this.storeName
          );
          try {
            db.createObjectStore(this.storeName, { keyPath: "key" });
          } catch (e) {
            logger.warn(
              "[ZustandIndexedDB] init: Could not create store",
              this.storeName,
              e
            );
          }
        }
      };

      request.onsuccess = () => {
        this.db = request.result;

        if (!this.db.objectStoreNames.contains(this.storeName)) {
          logger.error(
            "[ZustandIndexedDB] init: Store",
            this.storeName,
            "does not exist after main service init"
          );
          this.db.close();
          this.db = null;
          this.initPromise = null;
          reject(
            new Error(
              `Store ${this.storeName} does not exist. Database may not be properly initialized.`
            )
          );
        } else {
          this.initPromise = null;
          resolve();
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Gets an item from IndexedDB
   */
  async getItem(key: string): Promise<string | null> {
    await this.init();

    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error("Database not initialized"));
        return;
      }

      const transaction = this.db.transaction([this.storeName], "readonly");
      const store = transaction.objectStore(this.storeName);

      const request = store.get(key);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.value : null);
      };
    });
  }

  /**
   * Sets an item in IndexedDB
   */
  async setItem(key: string, value: string): Promise<void> {
    await this.init();

    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error("Database not initialized"));
        return;
      }

      const transaction = this.db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);

      const request = store.put({ key, value });

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  /**
   * Removes an item from IndexedDB
   */
  async removeItem(key: string): Promise<void> {
    await this.init();

    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error("Database not initialized"));
        return;
      }

      const transaction = this.db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);

      const request = store.delete(key);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }
}

/**
 * Creates a new Zustand IndexedDB storage instance for a specific store
 * @param storeName - The name of the store: "projects", "workspaces", or "datasets"
 */
export function createZustandIndexedDBStorage(
  storeName: "projects" | "workspaces" | "datasets"
): () => ZustandIndexedDBStorage {
  return () => new ZustandIndexedDBStorage(storeName);
}
